/**
 * Upsun Dispatch signups (DISP-1185, DISP-2660). A tab that arrives with
 * `signup_target=upsun_dispatch` is recorded in sessionStorage. The verification
 * email opens a fresh tab without it (AUTH-320), so a one-time, email-keyed
 * hand-off in localStorage carries it there.
 */

/** Contract with Dispatch's control plane. */
export const DISPATCH_SIGNUP_SOURCE = "upsun_dispatch";
export const SIGNUP_TARGET_PARAM = "signup_target";

const TAB_KEY = "dispatch-signup";
const HANDOFF_KEY = "dispatch-signup-email";

/** Where the Auth server lands the verification-email link (sarsen `signup-form-url`). */
const VERIFICATION_LANDING_PATH = "/register/form";

/** The two screens an organization invitation passes through, with `email_address` and `sid`. */
const INVITATION_PATHS = ["/register", "/register/form/password"];

const DISPATCH_PANE_MESSAGES = [
  "auth.left_panel.dispatch.title",
  "auth.left_panel.dispatch.description"
];

const withoutTrailingSlash = (pathname: string) => pathname.replace(/\/+$/, "");

const normalizeEmail = (email: string | null) =>
  email?.trim().toLowerCase() || null;

// Storage access can throw (Safari private mode, storage disabled). The signup
// must never break over presentation, so every failure reads as "not Dispatch".
const attempt = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export const isInvitationLanding = (
  pathname: string = window.location.pathname,
  search: string = window.location.search
): boolean => {
  const params = new URLSearchParams(search);
  return (
    INVITATION_PATHS.includes(withoutTrailingSlash(pathname)) &&
    params.has("email_address") &&
    params.has("sid")
  );
};

/** The one reader, for both the trial copy and the branded column. */
export const isDispatchSignup = (): boolean =>
  attempt(() => window.sessionStorage.getItem(TAB_KEY) === "1", false) &&
  !isInvitationLanding();

/** Whether the auth screens show the Dispatch column; vendors without its copy keep their own. */
export const showsDispatchPane = (messages: Record<string, unknown>) =>
  isDispatchSignup() && DISPATCH_PANE_MESSAGES.every(id => id in messages);

/**
 * Record this load's origin, from the URL the Auth UI was loaded with:
 * `signup_target` naming Dispatch marks the tab, any other value clears it, and
 * none leaves it alone — except on the verification-email landing, which
 * redeems a hand-off for the same email address.
 */
export const rememberDispatchSignup = (
  params: URLSearchParams,
  pathname: string = window.location.pathname
) => {
  const target = params.get(SIGNUP_TARGET_PARAM);
  attempt(() => {
    if (target === DISPATCH_SIGNUP_SOURCE) {
      window.sessionStorage.setItem(TAB_KEY, "1");
    } else if (target !== null) {
      window.sessionStorage.removeItem(TAB_KEY);
    } else if (withoutTrailingSlash(pathname) === VERIFICATION_LANDING_PATH) {
      const email = normalizeEmail(params.get("email_address"));
      if (email && window.localStorage.getItem(HANDOFF_KEY) === email) {
        window.sessionStorage.setItem(TAB_KEY, "1");
        window.localStorage.removeItem(HANDOFF_KEY);
      }
    }
  }, undefined);
};

/** A non-Dispatch signup for the same address clears the hand-off, so an abandoned one cannot dress it. */
export const handOffDispatchSignup = (email: string) => {
  const normalized = normalizeEmail(email);
  attempt(() => {
    if (normalized && isDispatchSignup()) {
      window.localStorage.setItem(HANDOFF_KEY, normalized);
    } else if (window.localStorage.getItem(HANDOFF_KEY) === normalized) {
      window.localStorage.removeItem(HANDOFF_KEY);
    }
  }, undefined);
};

export const forgetDispatchSignup = () => {
  attempt(() => window.sessionStorage.removeItem(TAB_KEY), undefined);
};
